import {
    AfterViewInit,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import { NgbActiveModal } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AbstractCmpsForm, CmpsFormFields } from '../common/common.form';
import { ScreenReaderLiveService } from '../common/screen-reader-live.service';
import { SecurityQuestion } from '../user/user.model';

import { TranslationKeyMap } from './auth.i18n';
import { ResetPassword, ResetPasswordSecQuesAns } from './auth.model';
import { AuthService } from './auth.service';

enum Steps {
    USERNAME,
    ANSWER,
    PASSWORD,
    DONE,
}

const UsernameQuestionResetFormFields: CmpsFormFields = {
    USERNAME: {
        id: 'username',
        name: 'username',
        label: TranslationKeyMap._72_USERNAME,
        placeholder: TranslationKeyMap._72_USERNAME,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
        },
    },
};

class UsernameQuestionResetForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(UsernameQuestionResetFormFields, _translateService);
    }

    get name(): string {
        return 'usernameQuestionResetForm';
    }
}

const AnswerQuestionResetFormFields: CmpsFormFields = {
    SECURITY_QUESTION: {
        id: 'securityQuestion',
        name: 'securityQuestion',
        label: TranslationKeyMap._706_SECURITY_QUESTION,
    },
    ANSWER: {
        id: 'answer',
        name: 'answer',
        label: TranslationKeyMap._707_ANSWER,
        placeholder: TranslationKeyMap._707_ANSWER,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._873_YOU_MUST_ENTER_THE_CORRECT_RESPONSE_TO_YOUR_SECURITY_QUESTION_IN_ORDER_TO_RESET_YOUR_PASSWORD_,
        },
    },
};

class AnswerQuestionResetForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(AnswerQuestionResetFormFields, _translateService);
    }

    get name(): string {
        return 'answerQuestionResetForm';
    }
}

const PasswordQuestionResetFormFields: CmpsFormFields = {
    NEW_PASSWORD: {
        id: 'newPassword',
        name: 'newPassword',
        label: TranslationKeyMap._692_NEW_PASSWORD,
        placeholder: TranslationKeyMap._692_NEW_PASSWORD,
        validators: [Validators.required, Validators.minLength(5)],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            minlength:
                TranslationKeyMap._837_PASSWORD_MUST_BE_A_MINIMUM_OF__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    NEW_PASSWORD_CONFIRM: {
        id: 'newPasswordConfirm',
        name: 'newPasswordConfirm',
        label: TranslationKeyMap._693_CONFIRM_NEW_PASSWORD,
        placeholder: TranslationKeyMap._693_CONFIRM_NEW_PASSWORD,
        // requiredWhenValidator, textEqualValidator
        validationMsgs: {
            requiredWhen: null,
            textEqual:
                TranslationKeyMap._653_PASSWORDS_DO_NOT_MATCH__PLEASE_TRY_AGAIN_,
        },
    },
};

class PasswordQuestionResetForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(PasswordQuestionResetFormFields, _translateService);
    }

    get name(): string {
        return 'passwordQuestionResetForm';
    }
}

@Component({
    selector: 'cmps-auth-reset-question',
    templateUrl: './question-reset.auth.component.html',
})
export class QuestionResetAuthComponent implements AfterViewInit, OnDestroy {
    @ViewChild('ngForm', { static: true })
    ngForm: NgForm;

    i18n = TranslationKeyMap;
    Steps = Steps;
    step: Steps;

    // models
    username: string;
    securityQuestion: SecurityQuestion;
    resetPasswordSecQuesAnsModel: ResetPasswordSecQuesAns;
    resetPasswordModel: ResetPassword;

    form:
        | UsernameQuestionResetForm
        | AnswerQuestionResetForm
        | PasswordQuestionResetForm;

    private _ngZoneSub: Subscription;
    private _sub: Subscription;
    success: boolean;
    failure: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        public activeModal: NgbActiveModal,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _screenReaderLiveService: ScreenReaderLiveService,
    ) {
        this.step = Steps.USERNAME;
        this.form = new UsernameQuestionResetForm(_translateService);
    }

    ngAfterViewInit(): void {
        this._screenReaderLiveService.announce(
            this._translateService.instant(
                TranslationKeyMap._943_ENTER_YOUR_USERNAME_AND_CLICK_THE__RETRIEVE_QUESTION__BUTTON_TO_RETRIEVE_YOUR_QUESTION__ONCE_YOUR_QUESTION_IS_RETRIEVED__ENTER_YOUR_ANSWER_AND_CLICK_THE__VERIFY_ANSWER__BUTTON__IF_YOUR_ANSWER_IS_CORRECT_YOU_WILL_THEN_BE_ABLE_TO_CHANGE_YOUR_PASSWORD_,
            ),
        );
        this._bindForm();
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
        if (this._ngZoneSub) {
            this._ngZoneSub.unsubscribe();
        }
    }

    private _bindForm(): void {
        this._ngZoneSub = this._ngZone.onStable
            .asObservable()
            .pipe(take(1))
            .subscribe(() => {
                if (this.ngForm) {
                    this.form.form = this.ngForm;
                    try {
                        this.form.bind();
                        this._focusStep();
                    } catch (err) {
                        console.error(err);
                    }
                }
            });
    }

    private _focusStep(): void {
        let fieldId;
        switch (this.step) {
            case Steps.USERNAME:
                fieldId = UsernameQuestionResetFormFields.USERNAME.id;
                break;
            case Steps.ANSWER:
                fieldId = AnswerQuestionResetFormFields.ANSWER.id;
                break;
            case Steps.PASSWORD:
                fieldId = PasswordQuestionResetFormFields.NEW_PASSWORD.id;
                break;
        }
        if (fieldId) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            this._el.nativeElement.querySelector(`#${fieldId}`).focus();
        }
    }

    onSubmit(): void {
        this.form.validate();
        if (!this.form.isValid()) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const error = (err) => {
            this.failure = true;
            this.form.serverError =
                err.message || TranslationKeyMap._704_UNKNOWN_ERROR;
            this.form.submitted = false;
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, no-return-assign
        const complete = () => (this.form.submitted = false);
        const languageId = parseInt(this._translateService.currentLang);

        // wait in the lobby until next step is set
        this._el.nativeElement.querySelector('#authQuestionResetLobby').focus();

        this.failure = false;
        this.form.submitted = true;
        switch (this.step) {
            case Steps.USERNAME:
                this._sub = this._authService
                    .retrieveSecurityQuestion(this.username, languageId)
                    .subscribe(
                        (securityQuestion) => {
                            this.securityQuestion = securityQuestion;
                            this.resetPasswordSecQuesAnsModel = {
                                answer: null,
                                userId: this.securityQuestion.userId,
                            };

                            this.step = Steps.ANSWER;
                            this.form.unbind();
                            this.form = new AnswerQuestionResetForm(
                                this._translateService,
                            );
                            this._screenReaderLiveService.announce(
                                this.securityQuestion.question,
                            );
                            this._bindForm();
                        },
                        error,
                        complete,
                    );
                break;
            case Steps.ANSWER:
                this.resetPasswordSecQuesAnsModel.languageId = languageId;
                this._sub = this._authService
                    .requestResetPasswordTokenByQuestion(
                        this.resetPasswordSecQuesAnsModel,
                    )
                    .subscribe(
                        (tokenHash) => {
                            this.resetPasswordModel = {
                                tokenHash,
                                password: null,
                            };

                            this.step = Steps.PASSWORD;
                            this.form.unbind();
                            this.form = new PasswordQuestionResetForm(
                                this._translateService,
                            );
                            this._bindForm();
                        },
                        error,
                        complete,
                    );
                break;
            case Steps.PASSWORD:
                this.resetPasswordModel.languageId = languageId;
                this._sub = this._authService
                    .resetPassword(this.resetPasswordModel)
                    .subscribe(
                        (ret) => {
                            this.success = true;

                            this.step = Steps.DONE;
                        },
                        error,
                        complete,
                    );
                break;
        }
    }
}
